import get from 'lodash/get';
import { createSelector } from 'reselect';

import { NAME } from './constants';

export const getModel = (state) => state[NAME];

export const getInitialValues = createSelector(
    getModel,
    (model) => model.initialValues,
);

// PROJECT LOCATION
export const getCurrentProjectLocation = createSelector(
    getModel,
    (model) => model.currentProjectLocation,
);

export const getCurrentProjectLocationData = createSelector(
    getCurrentProjectLocation,
    (model) => model.data,
);

// SELECTED SEGMENT
export const getSelectedSegment = createSelector(
    getModel,
    (model) => model.selectedSegment,
);

export const getSelectedSegmentId = createSelector(
    getSelectedSegment,
    (model) => get(model, 'id', null),
);

// SAVE SEGMENT
export const getCreateSegment = createSelector(
    getModel,
    (model) => model.createSegment,
);

export const getIsSavingSegment = createSelector(
    getCreateSegment,
    (model) => model.isSaving,
);

export const getSaveSegmentErrors = createSelector(
    getCreateSegment,
    (model) => model.errors,
);

// CHECK SEGMENT
export const getCheckSegment = createSelector(
    getModel,
    (model) => model.checkSegment,
);

export const getIsCheckinSegment = createSelector(
    getCheckSegment,
    (model) => model.isSaving,
);

export const getCheckSegmentErrors = createSelector(
    getCheckSegment,
    (model) => model.errors,
);

// DELETE SEGMENT
export const getDeleteSegment = createSelector(
    getModel,
    (model) => model.deleteSegment,
);

export const getIsDeletingSegment = createSelector(
    getDeleteSegment,
    (model) => model.isDeleting,
);

export const getDeleteSegmentErrors = createSelector(
    getDeleteSegment,
    (model) => model.errors,
);

// EDIT SEGMENT
export const getUpdateSegment = createSelector(
    getModel,
    (model) => model.updateSegment,
);

export const getIsEditingSegment = createSelector(
    getUpdateSegment,
    (model) => model.isSaving,
);

export const getEditSegmentData = createSelector(
    getUpdateSegment,
    (model) => model.data,
);

export const getEditSegmentErrors = createSelector(
    getUpdateSegment,
    (model) => model.errors,
);

// PARENT FIELD SEGMENT
export const getParentFieldSegment = createSelector(
    getModel,
    (model) => model.fetchParentFieldSegment,
);

export const getIsFetchingParentFieldSegment = createSelector(
    getParentFieldSegment,
    (value) => value.isFetching,
);

export const getParentFieldSegmentData = createSelector(
    getParentFieldSegment,
    (value) => value.data || {},
);

// SEGMENTS
export const getSegmentsData = createSelector(
    getParentFieldSegmentData,
    (values) => values.segments || [],
);

export const getParentFieldSegmentCounters = createSelector(
    getParentFieldSegmentData,
    (values) => ({
        solarModulesDesignNumber: values.solarModulesDesignNumber,
        solarModulesOfferNumber: values.solarModulesOfferNumber,
    }),
);

export const getParentFieldSegmentErrors = createSelector(
    getParentFieldSegment,
    (value) => value.errors,
);

// OFFER INFO PATTERN
export const getOfferInfoPatern = createSelector(
    getModel,
    (model) => model.offerInfoPatern,
);

export const getIsFetchingOfferInfoPatern = createSelector(
    getOfferInfoPatern,
    (value) => value.isFetching,
);

export const getOfferInfoPaternData = createSelector(
    getOfferInfoPatern,
    (value) => value.data,
);

// OFFER PANELS
export const getOfferPanels = createSelector(
    getModel,
    (model) => model.offerPanels,
);

export const getIsFetchingOfferPanels = createSelector(
    getOfferPanels,
    (offerInfo) => offerInfo.isFetching,
);

export const getOfferPanelsCounters = createSelector(
    getOfferPanels,
    (offerInfo) => offerInfo.counters || {},
);

export const getOfferPanelsData = createSelector(
    getOfferPanels,
    (offerInfo) => offerInfo.data || {},
);

export const getOfferPanelsErrors = createSelector(
    getOfferPanels,
    (offerInfo) => offerInfo.errors,
);

// PRESET
export const getPreset = createSelector(getModel, (model) => model.fetchPreset);

export const getIsFetchingPreset = createSelector(
    getPreset,
    (value) => value.isFetching,
);

export const getPresetData = createSelector(getPreset, (value) => value.data);

export const getPresetConfiguration = createSelector(
    getPresetData,
    (data) => data.preset_configuration,
);

// DRAWING MODE
export const getIsDrawingMode = createSelector(
    getModel,
    (model) => model.drawingMode,
);

// DRAWING TYPE
export const getDrawingType = createSelector(
    getModel,
    (model) => model.drawingType,
);

/** SHADOW SETUP */
export const getShaderSetttingsInitialValues = createSelector(
    getModel,
    (model) => model.shaderSettingsInitialValues,
);

export const getShaderSettings = createSelector(
    getModel,
    (model) => model.saveShaderSettings,
);

export const getShaderSettingsIsSaving = createSelector(
    getShaderSettings,
    (model) => model.isSaving,
);

export const getShaderSettingsData = createSelector(
    getShaderSettings,
    (model) => model.data,
);

export const getShaderSettingsErrors = createSelector(
    getShaderSettings,
    (model) => model.errors,
);

/** IMAGE UPLOAD */
export const getSaveImageModel = createSelector(
    getModel,
    (model) => model.saveImage,
);

export const getSaveImageIsSaving = createSelector(
    getSaveImageModel,
    (model) => model.isSaving,
);

export const getSaveImageData = createSelector(
    getSaveImageModel,
    (model) => model.data,
);

export const getSaveImageErrors = createSelector(
    getSaveImageModel,
    (model) => model.errors,
);

/** SELECT/COPY/PASTE */
export const getSelectedItems = createSelector(
    getModel,
    (model) => model.selectedItems,
);

export const getCopyBuffer = createSelector(
    getModel,
    (model) => model.copyBuffer,
);

/** DISABLED/ENABLE EDITION */
export const getIsDisabledEdition = createSelector(
    getModel,
    (model) => model.isDisabledEdition,
);

/** DISABLED/ENABLE MODULES */
export const getIsDisablingModules = createSelector(
    getModel,
    (model) => model.isDisablingModules,
);

// GENERATE PROPOSAL
export const getGenerateProposal = createSelector(
    getModel,
    (model) => model.generateProposal,
);

export const getIsGeneratingProposal = createSelector(
    getCreateSegment,
    (model) => model.isFetching,
);

// SIMULATION STATUS
export const getSimulationStatus = createSelector(
    getModel,
    (model) => model.simulation,
);

export const getSimulationIsPlaying = createSelector(
    getSimulationStatus,
    (model) => model.isPlaying,
);

export const getAddSegmentModalOpenStatus = createSelector(
    getModel,
    (model) => model.addSegmentModalOpenStatus,
);

export const getAddSegmentModalEditionMode = createSelector(
    getModel,
    (model) => model.addSegmentModalEditionMode ?? { isEditionMode: false },
);

export const getChangeModuleStatusAction = createSelector(
    getModel,
    (model) => model.changeModuleStatusAction,
);

export const getNewSegmentConfig = createSelector(
    getModel,
    (model) => model.newSegmentConfig,
);

export const getViewLayersStatus = createSelector(
    getModel,
    (model) => model.viewLayerStatus,
);

export const getAltitude = createSelector(getModel, (model) => model.altitude);
